import { MoreVertical } from "lucide-solid";
import {
	type Component,
	type ComponentProps,
	createMemo,
	Index,
	type JSXElement,
	Match,
	Show,
	Switch,
	splitProps
} from "solid-js";
import { Dynamic, Portal } from "solid-js/web";
import { twJoin, twMerge } from "tailwind-merge";
import PremiumIcon from "~/Icons/12_Icon_Premium.svg?component-solid";
import CreditsIcon from "~/Icons/16_Icon_Credits.svg?component-solid";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { Button, isUsercanRunPricedTask } from "~/solidJs/shared";
import { Badge } from "~/solidJs/shared/ui/atoms/Badge/Badge";
import Icons from "~/solidJs/shared/ui/atoms/IconsComp";
import { StyledTooltip } from "~/solidJs/shared/ui/atoms/Tooltip";
import { Menu } from "~/solidJs/shared/ui/molecules/Menu";
import { HoverFeatureMenuHandlers, HoverFeatureMenuMethods, HoverFeatureMenuModel } from ".";
import type { HoverFeatureAction } from "./model";

interface FeatureTooltipContentProps {
	action: HoverFeatureAction;
	hidePremiumIcon?: boolean;
}

const FeatureTooltipContent: Component<FeatureTooltipContentProps> = (props) => {
	return (
		<div class="flex flex-col gap-1">
			<div class="flex gap-1.5">
				<span class="typography-text-14-semibold flex items-center gap-2 text-white">
					{props.action.label}
					<Show when={props.action.disabled?.() && !props.hidePremiumIcon}>
						<Icons.Premium._12 />
					</Show>
				</span>
				<Show when={HoverFeatureMenuMethods.calculateActionCost(props.action)} keyed>
					{(cost) => (
						<Badge
							variant={
								isUsercanRunPricedTask(cost, getUserStore())
									? "creditsDark"
									: "creditsOrange"
							}
							size="small"
						>
							<CreditsIcon class="size-2.5" />
							{cost}
						</Badge>
					)}
				</Show>
			</div>
			<Show when={props.action.description}>
				<p class="caption-11-regular text-white">{props.action.description}</p>
			</Show>
		</div>
	);
};

interface HoverFeatureButtonProps {
	action: HoverFeatureAction;
	source?: "footer" | "header";
}

const HoverFeatureButton: Component<HoverFeatureButtonProps> = (props) => {
	const handleClick = (e: Event) => {
		e.preventDefault();
		e.stopPropagation();
		EventAnalytics.hoverUsed(props.action.id, props.source || "footer");
		if (props.action.disabled?.()) return;
		props.action.handler();
	};

	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();

	return (
		<StyledTooltip.TooltipClosedComponent
			portalMount={() => document.querySelector("#workspace-overlay") || document.body}
			showArrow
			content={<FeatureTooltipContent action={props.action} />}
		>
			{(p: any) => (
				<Button
					{...p}
					variant="Ghost"
					size="small"
					class={twJoin(
						"pointer-events-auto opacity-100",
						props.action.disabled?.() &&
							"bg-background-overlay-black-20 text-text-tertiary backdrop-blur-md"
					)}
					on:click={handleClick}
					disabled={props.action.disabled?.()}
				>
					<Show when={props.action.Icon}>
						<Dynamic class="size-3.5" component={props.action.Icon} />
					</Show>
					<Show
						when={methods.getMenuSize() === HoverFeatureMenuModel.HoverMenuSize.Large}
					>
						<span>{props.action.label}</span>
					</Show>
				</Button>
			)}
		</StyledTooltip.TooltipClosedComponent>
	);
};

const HoverFeatureTextButton: Component<HoverFeatureButtonProps> = (props) => {
	const handleClick = (e: Event) => {
		e.preventDefault();
		e.stopPropagation();
		// centralized analytics for text viewer actions (footer)
		EventAnalytics.hoverUsed(props.action.id, props.source || "footer");
		props.action.handler();
	};

	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();

	return (
		<Button
			variant="ButtonNode"
			class="pointer-events-auto"
			on:click={handleClick}
			disabled={props.action.disabled?.()}
		>
			<Show when={props.action.Icon}>
				<Dynamic class="size-3.5" component={props.action.Icon} />
			</Show>
			<Show when={methods.getMenuSize() === HoverFeatureMenuModel.HoverMenuSize.Large}>
				<span>{props.action.label}</span>
			</Show>
		</Button>
	);
};

type OverlayActionsHeaderProps = {
	children?: JSXElement;
};

const OverlayActionsHeader: Component<OverlayActionsHeaderProps> = (props) => {
	return (
		<div class="absolute top-0 right-0 left-0 flex items-center justify-between p-1.5 pb-12">
			<div
				class="hover-feature-backdrop absolute inset-0 backdrop-blur-[4px]"
				style={{
					background:
						"linear-gradient(0deg, rgba(0, 0, 0, 0) 2.09%, rgba(10, 10, 10, 0.1) 29.57%, rgba(20, 20, 20, 0.2) 14%, rgba(40, 40, 40, 0.4) 100%)",
					mask: "linear-gradient(rgb(255, 255, 255) 0%, rgba(255, 255, 255, 0.7) 40%, rgba(255, 255, 255, 0.3) 80%, rgba(255, 255, 255, 0) 100%)"
				}}
			/>
			<div class="relative z-10 flex w-full items-center justify-end gap-1.5">
				{props.children}
			</div>
		</div>
	);
};

type OverlayActionsFooterProps = {
	children?: JSXElement;
};

const OverlayActionsFooter: Component<OverlayActionsFooterProps> = (props) => {
	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();
	return (
		<div
			class={twMerge(
				"absolute bottom-0 left-0 flex w-full items-end justify-center gap-1.5 p-1.5 pt-8",
				methods.getMenuSize() === HoverFeatureMenuModel.HoverMenuSize.Medium && "px-3"
			)}
		>
			<div
				class="hover-feature-backdrop absolute inset-0 backdrop-blur-[4px]"
				style={{
					background:
						"linear-gradient(0deg, rgba(40, 40, 40, 0.5) 0%, rgba(10, 10, 10, 0.1) 65.38%, rgba(5, 5, 5, 0.01) 89.42%, rgba(0, 0, 0, 0) 100%)",
					mask: "linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.7) 65.38%, rgba(255, 255, 255, 0.3) 89.42%, rgba(255, 255, 255, 0) 100%)",
					"-webkit-mask":
						"linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.7) 65.38%, rgba(255, 255, 255, 0.3) 89.42%, rgba(255, 255, 255, 0) 100%)"
				}}
			/>

			<div class="relative z-10 flex w-full items-center justify-start gap-1.5">
				{props.children}
			</div>
		</div>
	);
};

export const RenderContextMenuItem: Component<{
	uiPorps: ComponentProps<"div">;
	action: HoverFeatureAction;
}> = (props) => {
	return (
		<StyledTooltip.TooltipClosedComponent
			showArrow
			portalMount={() => document.querySelector("#workspace-overlay") || document.body}
			rootProps={{ positioning: { placement: "right", offset: { mainAxis: 20 } } }}
			content={<FeatureTooltipContent hidePremiumIcon action={props.action} />}
		>
			{(tooltipProps) => {
				const [custom, rest] = splitProps(props.uiPorps, ["children", "onSelect"]);
				return (
					<div {...(tooltipProps as ComponentProps<"div">)}>
						<Menu.Item
							{...rest}
							disabled={props.action.disabled?.()}
							class={twJoin(props.action.disabled?.() && "text-text-tertiary")}
						>
							{custom.children}
							<Show
								when={HoverFeatureMenuMethods.calculateActionCost(props.action)}
								keyed
							>
								{(cost) => (
									<Badge
										class={twJoin(
											isUsercanRunPricedTask(cost, getUserStore()) &&
												"text-text-primary"
										)}
										variant={
											isUsercanRunPricedTask(cost, getUserStore())
												? "credits"
												: "creditsOrange"
										}
										size="small"
									>
										<CreditsIcon class="size-2.5" />
										{cost}
									</Badge>
								)}
							</Show>
							<Show when={props.action.disabled?.()} keyed>
								<PremiumIcon />
							</Show>
						</Menu.Item>
					</div>
				);
			}}
		</StyledTooltip.TooltipClosedComponent>
	);
};

// Components for rendering specific actions
const ContextMenuActions: Component = () => {
	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();
	return (
		<Show when={methods.contextMenuActions().length > 0}>
			<Menu.MenuClosedComponent
				portalMount={() => document.querySelector("#workspace-overlay") || document.body}
				contentClass="max-h-fit"
				items={methods.contextMenuActions()}
				onceRootProps={{
					onOpenChange: (d) => {
						if (d.open) {
							HoverFeatureMenuMethods.openContextMenu();
							return;
						}
						HoverFeatureMenuMethods.closeHoverFeatureMenu();
					},
					open: methods.isContextMenuOpen()
				}}
				trigger={(p) => (
					<Button
						variant="Ghost"
						size="small"
						{...p}
						class="pointer-events-auto"
						on:click={(e) => {
							e.stopPropagation();
							(p as any)?.onClick?.(e);
						}}
					>
						<MoreVertical size={20} />
					</Button>
				)}
			/>
		</Show>
	);
};

const FooterActions: Component = () => {
	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();
	return (
		<div
			class={twMerge(
				"relative z-10 flex w-full items-center justify-start gap-1.5",
				methods.getMenuSize() === HoverFeatureMenuModel.HoverMenuSize.Medium &&
					"justify-between"
			)}
		>
			<Index each={methods.footerActions()}>
				{(action) => <HoverFeatureButton action={action()} />}
			</Index>
		</div>
	);
};

const _SaveAction: Component = () => {
	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();
	const context = methods.context();
	if (!context) return null;

	const saveAction = HoverFeatureMenuHandlers.createSaveHoverFeature(context);

	return <HoverFeatureButton action={saveAction} source="header" />;
};

const ImageHoverFeatures = () => {
	return (
		<>
			<OverlayActionsHeader>
				<ContextMenuActions />
			</OverlayActionsHeader>

			<OverlayActionsFooter>
				<FooterActions />
			</OverlayActionsFooter>
		</>
	);
};

const VideoHoverFeatures = () => {
	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();
	return (
		<OverlayActionsHeader>
			<Index each={methods.footerActions()}>
				{(action) => <HoverFeatureButton action={action()} />}
			</Index>
		</OverlayActionsHeader>
	);
};

const TextHoverFeatures = () => {
	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();
	return (
		<div class="absolute bottom-0 left-0 flex w-full gap-1 p-2">
			<Index each={methods.footerActions()}>
				{(item) => <HoverFeatureTextButton action={item()} />}
			</Index>
		</div>
	);
};

const AudioHoverFeatures = () => {
	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();
	return (
		<div class="-bottom-2 absolute left-0 flex w-full gap-1 p-2 pl-2.5">
			<Index each={methods.footerActions()}>
				{(item) => <HoverFeatureTextButton action={item()} />}
			</Index>
		</div>
	);
};

type HoverFeatureMenuComponentProps = Record<string, never>;

const HoverFeatureMenuComponent: Component<HoverFeatureMenuComponentProps> = (_props) => {
	const methods = HoverFeatureMenuMethods.useHoverFeatureContext();
	const container = createMemo(() => methods.context()?.container || document.body);
	return (
		<Show when={methods.isOpen() && methods.context()}>
			<Portal mount={container()}>
				<div class="absolute top-0 left-0 h-full w-full animate-fade">
					<div
						class={twJoin(
							"relative h-full w-full overflow-hidden rounded-[10px]",
							methods.context()?.viewerType !==
								HoverFeatureMenuModel.ViewerType.Text &&
								methods.context()?.viewerType !==
									HoverFeatureMenuModel.ViewerType.Audio &&
								"bg-[rgb(0,0,0)]/5 outline outline-1 outline-border-secondary"
						)}
						style={{
							"will-change": "opacity",
							transform: "translateZ(0)",
							"backface-visibility": "hidden"
						}}
					>
						<Switch fallback={<ImageHoverFeatures />}>
							<Match
								when={
									methods.context()?.viewerType ===
									HoverFeatureMenuModel.ViewerType.Video
								}
							>
								<VideoHoverFeatures />
							</Match>
							<Match
								when={
									methods.context()?.viewerType ===
									HoverFeatureMenuModel.ViewerType.Text
								}
							>
								<TextHoverFeatures />
							</Match>
							<Match
								when={
									methods.context()?.viewerType ===
									HoverFeatureMenuModel.ViewerType.Audio
								}
							>
								<AudioHoverFeatures />
							</Match>
						</Switch>
					</div>
				</div>
			</Portal>
		</Show>
	);
};

export const HoverFeatureMenuPalette: Component = () => {
	const methods = HoverFeatureMenuMethods.useHoverFeatureMenu();

	return (
		<HoverFeatureMenuModel.HoverMenuContext.Provider value={methods}>
			<HoverFeatureMenuComponent />
		</HoverFeatureMenuModel.HoverMenuContext.Provider>
	);
};
